import type {
  ExtraConfig,
  LLMConfigFormData,
  LLMConfigPatchData,
  LLMConfigRequestData,
  LLMModel,
  ModelTypeKey,
  Provider,
  ShareUser,
} from "../types";

const AUTH_HEADER_NONE = "NONE";
export const TTS_RESPONSE_FORMAT = "wav";
export const STT_RESPONSE_FORMATS = ["text", "json", "verbose_json"] as const;
export const DEFAULT_STT_RESPONSE_FORMAT = STT_RESPONSE_FORMATS[0];

const STT_RESPONSE_FORMAT_SET = new Set<string>(STT_RESPONSE_FORMATS);

export const MODEL_TYPE_KEYS: ModelTypeKey[] = [
  "is_chat_model",
  "is_multimodal",
  "is_embedding_model",
  "is_tts_model",
  "is_stt_model",
  "is_image_generation_model",
];

const EXTRA_CONFIG_FIELDS_BY_MODEL_TYPE: Record<
  ModelTypeKey,
  (keyof ExtraConfig)[]
> = {
  is_chat_model: ["context_length", "max_output_tokens", "top_p"],
  is_multimodal: [
    "context_length",
    "max_output_tokens",
    "top_p",
    "max_image_size",
    "max_images_per_request",
  ],
  is_embedding_model: ["max_input_tokens"],
  is_tts_model: ["max_input_characters", "supported_formats"],
  is_stt_model: [
    "max_audio_duration_seconds",
    "max_audio_size",
    "supported_audio_format",
  ],
  is_image_generation_model: ["output_formats"],
};

export const EXTRA_CONFIG_FORM_FIELDS = [
  ...new Set(Object.values(EXTRA_CONFIG_FIELDS_BY_MODEL_TYPE).flat()),
] as (keyof ExtraConfig)[];

export const defaultProvider: Provider = {
  id: "private",
  name: "private",
  supports_image_generation: true,
  supports_imagecreation: true,
  supports_chat: true,
  supports_embedding: true,
  supports_generation: true,
  supports_multimodal: true,
  supports_stt: true,
  supports_tts: true,
};

export const getInitialFormData = (isPublic: boolean): LLMConfigFormData => ({
  provider: "",
  model: "",
  temperature: 0.7,
  max_tokens: 0,
  endpoint: "",
  auth_header_type: "bearer",
  is_chat_model: true,
  is_image_generation_model: false,
  is_embedding_model: false,
  is_stt_model: false,
  is_tts_model: false,
  embedding_dimension: "",
  name: "",
  is_default: false,
  api_key: "",
  project_id: "",
  is_multimodal: false,
  is_public: Boolean(isPublic),
  context_length: "",
  max_output_tokens: "",
  max_input_tokens: "",
  top_p: "",
  max_image_size: "",
  max_images_per_request: "",
  max_input_characters: "",
  supported_formats: [TTS_RESPONSE_FORMAT],
  max_audio_duration_seconds: "",
  max_audio_size: "",
  supported_audio_format: [],
  output_formats: [],
});

export const getApiErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const apiError = error as {
    response?: { data?: { detail?: unknown } };
    message?: string;
  };
  const detail = apiError?.response?.data?.detail;

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        return item?.msg || item?.message || String(item);
      })
      .join(", ");
  }

  return (
    (typeof detail === "string" && detail) || apiError?.message || fallback
  );
};

export const toTestId = (value: unknown): string =>
  String(value || "unknown").replace(/[^a-zA-Z0-9_-]/g, "-");

export const normalizeEmail = (value: string): string =>
  value.trim().toLowerCase();

export const getUserId = (user: ShareUser): string => user.user_id || "";

export const getUserEmail = (user: ShareUser): string =>
  user.user_email || user.email || "";

export const getUserName = (user: ShareUser, fallback: string): string =>
  user.user_name || user.name || getUserEmail(user) || fallback;

export const getUserInitial = (user: ShareUser, fallback: string): string =>
  getUserName(user, fallback).charAt(0).toUpperCase();

const numberOrEmpty = (value: string | number | undefined): number | "" =>
  value === undefined || value === "" ? "" : Number(value);

const normalizeEndpoint = (endpoint: string): string =>
  endpoint.trim().replace(/\/+$/, "");

const hasFieldValue = (value: string | number): boolean => value !== "";

const numberOrUndefined = (value: string | number): number | undefined => {
  if (!hasFieldValue(value)) return undefined;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
};

export const getModelTypeKey = (model: LLMModel): ModelTypeKey => {
  if (model.is_multimodal) return "is_multimodal";
  if (model.is_embedding_model) return "is_embedding_model";
  if (model.is_stt_model) return "is_stt_model";
  if (model.is_tts_model) return "is_tts_model";
  if (model.is_image_generation_model) return "is_image_generation_model";
  return "is_chat_model";
};

const getFirstString = (value: unknown): string | undefined => {
  if (typeof value === "string") return value;
  if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  return undefined;
};

export const normalizeSttResponseFormat = (value: unknown): string => {
  return (
    normalizeOptionalSttResponseFormat(value) ?? DEFAULT_STT_RESPONSE_FORMAT
  );
};

export const normalizeOptionalSttResponseFormat = (
  value: unknown,
): string | undefined => {
  const responseFormat = getFirstString(value)?.trim().toLowerCase();
  return responseFormat && STT_RESPONSE_FORMAT_SET.has(responseFormat)
    ? responseFormat
    : undefined;
};

export const getFormDataModelTypeKey = (
  formData: Pick<LLMConfigFormData, ModelTypeKey>,
): ModelTypeKey =>
  MODEL_TYPE_KEYS.find((key) => formData[key]) || "is_chat_model";

export const getExtraConfigFieldsForModelType = (
  modelType: ModelTypeKey,
): (keyof ExtraConfig)[] => EXTRA_CONFIG_FIELDS_BY_MODEL_TYPE[modelType];

export const getDefaultExtraConfigFormValues = (): Pick<
  LLMConfigFormData,
  (typeof EXTRA_CONFIG_FORM_FIELDS)[number]
> => {
  const initialFormData = getInitialFormData(false);

  return EXTRA_CONFIG_FORM_FIELDS.reduce(
    (values, field) => ({
      ...values,
      [field]: initialFormData[field],
    }),
    {} as Pick<LLMConfigFormData, (typeof EXTRA_CONFIG_FORM_FIELDS)[number]>,
  );
};

export const buildFormDataFromModel = (
  model: LLMModel,
  isPublic: boolean,
): LLMConfigFormData => {
  const extraConfig = model.extra_config || {};

  return {
    ...getInitialFormData(isPublic),
    provider: model.provider ?? "private",
    model: model.model ?? "",
    temperature: model.temperature ?? 0.7,
    max_tokens: model.max_tokens ?? 0,
    endpoint: model.endpoint ?? "",
    auth_header_type: model.auth_header_type ?? "bearer",
    is_image_generation_model: model.is_image_generation_model ?? false,
    is_chat_model: model.is_chat_model ?? false,
    is_embedding_model: model.is_embedding_model ?? false,
    is_stt_model: model.is_stt_model ?? false,
    is_tts_model: model.is_tts_model ?? false,
    embedding_dimension: model.embedding_dimension ?? "",
    name: model.name ?? "",
    is_default: model.is_default ?? false,
    api_key: "",
    project_id: model.project_id ?? "",
    is_multimodal: model.is_multimodal ?? false,
    is_public: Boolean(model.is_public),
    context_length: numberOrEmpty(extraConfig.context_length),
    max_output_tokens: numberOrEmpty(extraConfig.max_output_tokens),
    max_input_tokens: numberOrEmpty(extraConfig.max_input_tokens),
    top_p: numberOrEmpty(extraConfig.top_p),
    max_image_size: numberOrEmpty(extraConfig.max_image_size),
    max_images_per_request: numberOrEmpty(extraConfig.max_images_per_request),
    max_input_characters: numberOrEmpty(extraConfig.max_input_characters),
    supported_formats: model.is_tts_model
      ? [TTS_RESPONSE_FORMAT]
      : extraConfig.supported_formats ?? [],
    max_audio_duration_seconds: numberOrEmpty(
      extraConfig.max_audio_duration_seconds,
    ),
    max_audio_size: numberOrEmpty(extraConfig.max_audio_size),
    supported_audio_format: model.is_stt_model
      ? [
          normalizeOptionalSttResponseFormat(
            extraConfig.supported_audio_format,
          ),
        ].filter((format): format is string => Boolean(format))
      : extraConfig.supported_audio_format ?? [],
    output_formats: extraConfig.output_formats ?? [],
  };
};

export const buildRequestData = (
  formData: LLMConfigFormData,
  isPublic: boolean,
): LLMConfigRequestData => {
  const extra_config: ExtraConfig = {};
  const activeModelType = getFormDataModelTypeKey(formData);
  const activeExtraConfigFields = new Set(
    getExtraConfigFieldsForModelType(activeModelType),
  );
  const contextLength = numberOrUndefined(formData.context_length);
  const maxOutputTokens = numberOrUndefined(formData.max_output_tokens);
  const maxInputTokens = numberOrUndefined(formData.max_input_tokens);
  const topP = numberOrUndefined(formData.top_p);
  const maxImageSize = numberOrUndefined(formData.max_image_size);
  const maxImagesPerRequest = numberOrUndefined(
    formData.max_images_per_request,
  );
  const maxInputCharacters = numberOrUndefined(formData.max_input_characters);
  const maxAudioDurationSeconds = numberOrUndefined(
    formData.max_audio_duration_seconds,
  );
  const maxAudioSize = numberOrUndefined(formData.max_audio_size);

  if (
    formData.auth_header_type !== AUTH_HEADER_NONE &&
    formData.api_key.trim()
  ) {
    extra_config.api_key = formData.api_key.trim();
  }
  if (
    activeExtraConfigFields.has("context_length") &&
    contextLength !== undefined
  )
    extra_config.context_length = contextLength;
  if (
    activeExtraConfigFields.has("max_output_tokens") &&
    maxOutputTokens !== undefined
  )
    extra_config.max_output_tokens = maxOutputTokens;
  if (
    activeExtraConfigFields.has("max_input_tokens") &&
    maxInputTokens !== undefined
  )
    extra_config.max_input_tokens = maxInputTokens;
  if (activeExtraConfigFields.has("top_p") && topP !== undefined)
    extra_config.top_p = topP;
  if (
    activeExtraConfigFields.has("max_image_size") &&
    maxImageSize !== undefined
  )
    extra_config.max_image_size = maxImageSize;
  if (
    activeExtraConfigFields.has("max_images_per_request") &&
    maxImagesPerRequest !== undefined
  )
    extra_config.max_images_per_request = maxImagesPerRequest;
  if (
    activeExtraConfigFields.has("max_input_characters") &&
    maxInputCharacters !== undefined
  )
    extra_config.max_input_characters = maxInputCharacters;
  if (activeModelType === "is_tts_model")
    extra_config.supported_formats = [TTS_RESPONSE_FORMAT];
  if (
    activeExtraConfigFields.has("max_audio_duration_seconds") &&
    maxAudioDurationSeconds !== undefined
  )
    extra_config.max_audio_duration_seconds = maxAudioDurationSeconds;
  if (
    activeExtraConfigFields.has("max_audio_size") &&
    maxAudioSize !== undefined
  )
    extra_config.max_audio_size = maxAudioSize;
  if (activeModelType === "is_stt_model") {
    const responseFormat = normalizeOptionalSttResponseFormat(
      formData.supported_audio_format,
    );
    if (responseFormat) extra_config.supported_audio_format = [responseFormat];
  }
  if (
    activeExtraConfigFields.has("output_formats") &&
    formData.output_formats.length
  )
    extra_config.output_formats = formData.output_formats;

  const requestData: LLMConfigRequestData = {
    provider: formData.provider,
    model: formData.model.trim(),
    temperature: Number(formData.temperature),
    max_tokens: Number(formData.max_tokens) || 0,
    extra_config,
    endpoint: normalizeEndpoint(formData.endpoint),
    auth_header_type: formData.auth_header_type.trim(),
    is_image_generation_model: formData.is_image_generation_model,
    is_chat_model: formData.is_chat_model,
    is_embedding_model: formData.is_embedding_model,
    name: formData.name.trim(),
    is_default: formData.is_default,
    is_multimodal: formData.is_multimodal,
    is_stt_model: formData.is_stt_model,
    is_tts_model: formData.is_tts_model,
  };

  if (formData.is_embedding_model && formData.embedding_dimension !== "") {
    requestData.embedding_dimension = Number(formData.embedding_dimension);
  }

  if (!isPublic) requestData.project_id = formData.project_id;
  if (isPublic) requestData.is_public = true;

  return requestData;
};

const valuesEqual = (left: unknown, right: unknown): boolean => {
  if (Object.is(left, right)) return true;

  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => valuesEqual(value, right[index]))
    );
  }

  if (
    left !== null &&
    right !== null &&
    typeof left === "object" &&
    typeof right === "object"
  ) {
    const leftRecord = left as Record<string, unknown>;
    const rightRecord = right as Record<string, unknown>;
    const keys = new Set([
      ...Object.keys(leftRecord),
      ...Object.keys(rightRecord),
    ]);

    return [...keys].every((key) =>
      valuesEqual(leftRecord[key], rightRecord[key]),
    );
  }

  return false;
};

const UNCHANGED = Symbol("unchanged");

const getChangedValue = (
  current: unknown,
  original: unknown,
): unknown | typeof UNCHANGED => {
  if (valuesEqual(current, original)) return UNCHANGED;

  if (
    current !== null &&
    original !== null &&
    !Array.isArray(current) &&
    !Array.isArray(original) &&
    typeof current === "object" &&
    typeof original === "object"
  ) {
    const currentRecord = current as Record<string, unknown>;
    const originalRecord = original as Record<string, unknown>;

    return [...new Set([...Object.keys(currentRecord), ...Object.keys(originalRecord)])]
      .reduce<Record<string, unknown>>((patch, key) => {
        const changedValue = getChangedValue(
          currentRecord[key],
          originalRecord[key],
        );
        if (changedValue !== UNCHANGED) {
          patch[key] = changedValue === undefined ? null : changedValue;
        }
        return patch;
      }, {});
  }

  return current === undefined ? null : current;
};

/**
 * Builds the partial-update body expected by the LLM config PATCH endpoint.
 * Most nested objects are diffed recursively. extra_config is sent as the
 * complete current active-capability config because the backend replaces it.
 */
export const buildPatchData = (
  current: LLMConfigRequestData,
  original: LLMConfigRequestData,
): LLMConfigPatchData => {
  const changedValue = getChangedValue(current, original);
  if (changedValue === UNCHANGED) return {};

  const patchData = changedValue as LLMConfigPatchData;
  if (current.embedding_dimension === undefined) {
    delete patchData.embedding_dimension;
  }
  if (!valuesEqual(current.extra_config, original.extra_config)) {
    patchData.extra_config = current.extra_config;
  }

  return patchData;
};

export const sortModelsByName = (
  models: LLMModel[],
  order: "asc" | "desc",
): LLMModel[] =>
  [...models].sort((a, b) =>
    order === "asc"
      ? a.name.localeCompare(b.name)
      : b.name.localeCompare(a.name),
  );
